<template>
  <div class="image-element-wrapper">
    <div class="element-header">
      <span class="element-type-badge">
        <i class="fas fa-image"></i>
        <span>Image</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <i class="fas fa-copy"></i>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>
     
    <div class="image-element" @click="uploadImage">
      <div v-if="element.link || localImageUrl" class="image-container">
        <img :src="localImageUrl || element.link" alt="Image" class="uploaded-image" />
        <div class="image-overlay">
          <button class="change-image-btn" title="Changer l'image" :disabled="isUploading">
            <svg v-if="!isUploading" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
              <circle cx="12" cy="13" r="4"></circle>
            </svg>
            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="uploading-spinner">
              <circle cx="12" cy="12" r="10" opacity="0.3"></circle>
              <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
            </svg>
          </button>
        </div>
      </div>
      <div v-else class="image-placeholder" :class="{ 'uploading': isUploading }">
        <svg v-if="!isUploading" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
        <svg v-else width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="uploading-spinner">
          <circle cx="12" cy="12" r="10" opacity="0.3"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
        </svg>
        <p v-if="!isUploading">Cliquez pour ajouter une image</p>
        <p v-else>Upload en cours...</p>
      </div>
      <input type="file" ref="imageUpload" style="display: none" accept=".png,.jpg,.jpeg,image/png,image/jpeg,image/jpg" @change="handleImageUpload" />
    </div>
  </div>
</template>

<script>
import { axiosPeelo } from '@/utils/axios'
import { apiBaseUrl } from '@/config'

export default {
  name: 'ImageElement',
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isUploading: false,
      localImageUrl: null // URL locale pour afficher l'image sélectionnée
    }
  },
  methods: {
    // Méthode pour déclencher l'upload d'image
    uploadImage() {
      // Créer un élément input file dynamique
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.png,.jpg,.jpeg,image/png,image/jpeg,image/jpg';
      fileInput.style.display = 'none';
      
      // Ajouter l'événement de changement
      fileInput.addEventListener('change', (event) => {
        this.handleImageUpload(event);
      });
      
      // Ajouter au DOM, déclencher le clic, puis supprimer
      document.body.appendChild(fileInput);
      fileInput.click();
      setTimeout(() => {
        document.body.removeChild(fileInput);
      }, 1000);
    },
    
    // Méthode pour gérer l'upload d'image
    async handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Vérifier spécifiquement les fichiers CSV (interdits)
      const fileName = file.name.toLowerCase();
      const isCSV = fileName.endsWith('.csv') || 
                    file.type === 'text/csv' || 
                    file.type === 'application/csv' ||
                    file.type === 'text/comma-separated-values';
      
      if (isCSV) {
        this.$emit('notification', {
          message: "Les fichiers CSV ne sont pas autorisés. Veuillez sélectionner une image (JPG, PNG, GIF, etc.)",
          type: "error"
        });
        return;
      }
      
      // Vérifier que c'est bien une image avec les formats autorisés
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const allowedExtensions = ['.jpg', '.jpeg', '.png'];
      
      const isValidType = allowedTypes.includes(file.type);
      const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!isValidType || !isValidExtension) {
        this.$emit('notification', {
          message: "Format non autorisé. Seuls les fichiers PNG, JPG et JPEG sont acceptés.",
          type: "error"
        });
        return;
      }
      
      // Vérifier la taille du fichier (5 Mo maximum)
      const maxSizeInBytes = 5 * 1024 * 1024; // 5 Mo en bytes
      if (file.size > maxSizeInBytes) {
        const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
        this.$emit('notification', {
          message: `L'image est trop volumineuse (${fileSizeInMB} Mo). Taille maximum autorisée : 5 Mo`,
          type: "error"
        });
        return;
      }
      
      // Démarrer l'état de chargement
      this.isUploading = true;
      
      // Créer une URL locale pour afficher l'image immédiatement
      this.localImageUrl = URL.createObjectURL(file);
       
      try {
        // Créer FormData pour l'upload
        const formData = new FormData();
        formData.append('imagefromplayground', file);
        
       
        
        // Récupérer le token d'authentification
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Session expirée, veuillez vous reconnecter');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        // Configuration des headers
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        };
         // Envoyer l'image au serveur
        const response = await axiosPeelo.post(
          '/strong/upload/image/block',
          formData,
          config
        );
          
        if (response.data && response.data.success) {
          // Construire l'URL complète de l'image avec apiBaseUrl
          const imageUrl = `${apiBaseUrl}/${response.data.filePath}`;
           
          await new Promise(resolve => setTimeout(resolve, 5000));
           // Mettre à jour l'élément avec le lien de l'image du serveur
          const updatedElement = { ...this.element, link: imageUrl };
          
          // Émettre l'événement de mise à jour
          this.$emit('update', updatedElement);
          
          // Émettre une notification de succès
         
          
         } else {
          throw new Error(response.data?.message || 'Erreur lors de l\'upload');
        }
        
      } catch (error) {
        console.error('❌ Erreur lors de l\'upload de l\'image:', error);
        
        let errorMessage = 'Erreur lors de l\'upload de l\'image';
        
        if (error.response) {
          // Erreur de réponse du serveur
          errorMessage = error.response.data?.message || 
                        error.response.data?.error || 
                        `Erreur serveur (${error.response.status})`;
        } else if (error.request) {
          // Erreur de réseau
          errorMessage = 'Impossible de contacter le serveur';
        } else {
          // Autre erreur
          errorMessage = error.message || errorMessage;
        }
        
        // Émettre une notification d'erreur
        this.$emit('notification', {
          message: errorMessage,
          type: "error"
        });
        
      } finally {
        // Arrêter l'état de chargement
        this.isUploading = false;
      }
    }
  },
  
  // Nettoyer l'URL locale quand le composant est détruit
  beforeUnmount() {
    if (this.localImageUrl) {
      URL.revokeObjectURL(this.localImageUrl);
    }
  }
}
</script>

<style scoped>
.image-element-wrapper {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(249, 250, 251, 0.9));
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  margin-bottom: 20px;
}

.image-element-wrapper:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(145deg, rgba(249, 250, 251, 0.8), rgba(243, 244, 246, 0.6));
  border-bottom: 1px solid rgba(229, 231, 235, 0.5);
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #7c3aed;
  font-size: 0.875rem;
}

.element-type-badge i {
  color: #7c3aed;
}

.element-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.8));
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.action-btn.delete-btn:hover {
  background: linear-gradient(145deg, rgba(239, 68, 68, 0.15), rgba(239, 68, 68, 0.05));
  color: #ef4444;
}

.image-element {
  padding: 16px;
  cursor: pointer;
}

.image-placeholder {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  color: #666;
  border: 2px dashed #dee2e6;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
}

.image-placeholder:hover {
  background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
  border-color: #adb5bd;
}

.image-placeholder p {
  margin: 0;
  font-size: 0.9rem;
}

.image-container {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
}

.uploaded-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-container:hover .image-overlay {
  opacity: 1;
}

.change-image-btn {
  background: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  color: #667eea;
  transition: all 0.2s ease;
}

.change-image-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.change-image-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.uploading-spinner {
  animation: spin 1s linear infinite;
}

.image-placeholder.uploading {
  background: linear-gradient(135deg, #f0f9ff 0%, #dbeafe 100%);
  border-color: #3b82f6;
  color: #2563eb;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
